import { useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Breakpoints following Tailwind CSS conventions
 */
export const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Performance-optimized hook to detect current screen size
 * Uses useMemo and useCallback to prevent unnecessary re-renders
 */
export function useResponsive() {
  const [windowSize, setWindowSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let timeoutId: NodeJS.Timeout;
    
    // Optimized debounced resize handler
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, 150);
    };

    // Use passive event listener for better performance
    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { width, height } = windowSize;

  // Memoize all computed values to prevent recalculation
  return useMemo(() => ({
    // Raw dimensions
    width,
    height,
    
    // Device type checks
    isMobile: width < BREAKPOINTS.md,
    isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isDesktop: width >= BREAKPOINTS.lg,
    isLargeDesktop: width >= BREAKPOINTS.xl,
    isExtraLargeDesktop: width >= BREAKPOINTS['2xl'],
    
    // Breakpoint checks
    isXs: width < BREAKPOINTS.sm,
    isSm: width >= BREAKPOINTS.sm && width < BREAKPOINTS.md,
    isMd: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
    isLg: width >= BREAKPOINTS.lg && width < BREAKPOINTS.xl,
    isXl: width >= BREAKPOINTS.xl && width < BREAKPOINTS['2xl'],
    is2xl: width >= BREAKPOINTS['2xl'],
    
    // Minimum breakpoint checks
    isSmUp: width >= BREAKPOINTS.sm,
    isMdUp: width >= BREAKPOINTS.md,
    isLgUp: width >= BREAKPOINTS.lg,
    isXlUp: width >= BREAKPOINTS.xl,
    is2xlUp: width >= BREAKPOINTS['2xl'],
    
    // Maximum breakpoint checks
    isSmDown: width < BREAKPOINTS.md,
    isMdDown: width < BREAKPOINTS.lg,
    isLgDown: width < BREAKPOINTS.xl,
    isXlDown: width < BREAKPOINTS['2xl'],
    
    // Orientation
    isPortrait: height > width,
    isLandscape: width > height,
    
    // Utility functions (memoized with useCallback would be better but can't nest hooks)
    getCurrentBreakpoint: (): Breakpoint => {
      if (width >= BREAKPOINTS['2xl']) return '2xl';
      if (width >= BREAKPOINTS.xl) return 'xl';
      if (width >= BREAKPOINTS.lg) return 'lg';
      if (width >= BREAKPOINTS.md) return 'md';
      if (width >= BREAKPOINTS.sm) return 'sm';
      return 'xs';
    },
    
    isBreakpoint: (breakpoint: Breakpoint) => {
      const bp = BREAKPOINTS[breakpoint];
      const nextBreakpointKey = Object.keys(BREAKPOINTS)[
        Object.keys(BREAKPOINTS).indexOf(breakpoint) + 1
      ] as Breakpoint | undefined;
      
      if (!nextBreakpointKey) {
        return width >= bp;
      }
      
      return width >= bp && width < BREAKPOINTS[nextBreakpointKey];
    },
    
    isBreakpointUp: (breakpoint: Breakpoint) => {
      return width >= BREAKPOINTS[breakpoint];
    },
    
    isBreakpointDown: (breakpoint: Breakpoint) => {
      const nextBreakpointKey = Object.keys(BREAKPOINTS)[
        Object.keys(BREAKPOINTS).indexOf(breakpoint) + 1
      ] as Breakpoint | undefined;
      
      if (!nextBreakpointKey) return true;
      return width < BREAKPOINTS[nextBreakpointKey];
    },
    
    // Grid columns helper
    getGridColumns: (defaults: { mobile?: number; tablet?: number; desktop?: number }) => {
      if (width < BREAKPOINTS.md) return defaults.mobile || 1;
      if (width < BREAKPOINTS.lg) return defaults.tablet || 2;
      return defaults.desktop || 3;
    },
  }), [width, height]);
}

/**
 * Performance-optimized hook for responsive sidebar behavior
 */
export function useResponsiveSidebar() {
  const { isMobile, isTablet, isDesktop } = useResponsive();
  const [isOpen, setIsOpen] = useState(() => !isMobile && !isTablet);
  const [isCollapsed, setIsCollapsed] = useState(() => isTablet);

  useEffect(() => {
    // Auto-adjust sidebar based on screen size
    if (isMobile) {
      setIsOpen(false);
      setIsCollapsed(false);
    } else if (isTablet) {
      setIsOpen(true);
      setIsCollapsed(true);
    } else if (isDesktop) {
      setIsOpen(true);
      setIsCollapsed(false);
    }
  }, [isMobile, isTablet, isDesktop]);

  // Memoize callbacks
  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);
  const toggleCollapse = useCallback(() => setIsCollapsed(prev => !prev), []);

  return useMemo(() => ({
    isOpen,
    isCollapsed,
    isMobile,
    isTablet,
    isDesktop,
    toggleOpen,
    toggleCollapse,
    setIsOpen,
    setIsCollapsed,
  }), [isOpen, isCollapsed, isMobile, isTablet, isDesktop, toggleOpen, toggleCollapse]);
}

/**
 * Hook for responsive grid layouts
 */
export function useResponsiveGrid(options?: {
  mobile?: number;
  tablet?: number;
  desktop?: number;
  largeDesktop?: number;
}) {
  const { width } = useResponsive();
  
  return useMemo(() => {
    const getColumns = () => {
      if (width >= BREAKPOINTS.xl) return options?.largeDesktop || 4;
      if (width >= BREAKPOINTS.lg) return options?.desktop || 3;
      if (width >= BREAKPOINTS.md) return options?.tablet || 2;
      return options?.mobile || 1;
    };
    
    return {
      columns: getColumns(),
      gridClassName: `grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-${options?.tablet || 2} lg:grid-cols-${options?.desktop || 3} xl:grid-cols-${options?.largeDesktop || 4}`,
    };
  }, [width, options]);
}